import type { SlateElement } from '../scene/types';
import { isText } from '../scene/types';
import { IconClose } from './icons';

interface Props {
  readonly elements: readonly SlateElement[];
  readonly selectedIds: readonly string[];
  readonly labels: ReadonlyMap<string, string>;
  readonly onSelect: (id: string) => void;
  readonly onClose: () => void;
}

const TYPE_NAMES: Record<SlateElement['type'], string> = {
  rect: 'Rectangle',
  ellipse: 'Ellipse',
  diamond: 'Diamond',
  arrow: 'Arrow',
  freedraw: 'Drawing',
  text: 'Text',
};

/**
 * The keyboard and screen-reader route into the board. Every element is a real
 * button, so the whole document is reachable without a pointer.
 */
export function ContentsPanel({ elements, selectedIds, labels, onSelect, onClose }: Props) {
  const rows = elements.filter((el) => !el.isDeleted && !(isText(el) && el.containerId)).reverse();

  return (
    <aside className="side-panel" aria-label="Board contents">
      <header>
        <span>Board contents ({rows.length})</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Hide board contents">
          <IconClose />
        </button>
      </header>
      <div className="scroll">
        {rows.length === 0 ? (
          <p className="prose" style={{ padding: '8px 4px' }}>
            This board is empty. Pick a shape tool and drag on the canvas.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rows.map((el) => {
              const label = labels.get(el.id) ?? '';
              const description = isText(el) ? el.text.slice(0, 60) : label;
              return (
                <li key={el.id}>
                  <button
                    type="button"
                    className="element-row"
                    aria-current={selectedIds.includes(el.id)}
                    onClick={() => onSelect(el.id)}
                  >
                    <span className="type">{TYPE_NAMES[el.type]}</span>
                    <span className="desc">
                      {description.length > 0 ? description : <em style={{ opacity: 0.6 }}>no label</em>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="prose" style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: 12 }}>
        Select a row, then <kbd>Arrows</kbd> to nudge, <kbd>Shift</kbd> + <kbd>Arrows</kbd> for larger
        steps, and <kbd>Enter</kbd> to edit its text.
      </div>
    </aside>
  );
}
